import { LabeledField } from '@tableau/tableau-ui';
import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { DropdownArrowIcon, DynamicSvgIcon, IconDropdownArrowIcon } from '../../icons/Icons';
import { PopupStyle } from '../../styles/Standard';
import { generateId } from '../../utils/DomUtil';
import { FocusTrap, FocusTrapProps } from '../../utils/FocusTrap';
import { GlobalPointerDownOutsideListener } from '../../utils/GlobalPointerDownOutsideListener';
import { ButtonAttrs } from '../../utils/HtmlAttrs';
import { InteractionState } from '../../utils/InteractionState';
import { assignRef, AxeOptOut, EventUtilsOptOut, mergeEventHandlers } from '../../utils/ReactHelpers';
import { StackingComponentProps, StackingRootContext } from '../../utils/StackingComponent';
import { Img } from '../DomWidgets';
import * as DropdownButtonStyles from './DropdownButtonStyles';
import { DropdownOverlay } from './DropdownOverlay';

export enum DropdownButtonType {
  Outline,
  Line,
  Text,
  Icon,
}
/** careful, these assume numeric values for DropdownButtonType enum */
const TypeNames = ['outline', 'line', 'text', 'icon'];
const DisplayFlex = TypeStyle.style({ display: 'flex' });

export type DropdownButtonProps = Pick<StackingComponentProps, 'stackingRootId' | 'zIndex'> & {
  /**
   * The popup content provided in `popupContent`/`renderPopupContent` needs to have a `role` attribute which
   * matches the allowed values for `aria-haspopup`, e.g. `"listbox"`, `"dialog"`, etc. The same
   * element (with `role`) must also have `id`, which this `aria-controls` prop should be set to.
   *
   * @example
   * const popupId = generateSomeUniqueId();
   * return <DropdownButton
   *   aria-controls={popupId}
   *   aria-haspopup='dialog'
   *   label='Dropdown Button with a Dialog Popup'
   *   popupContent={<div id={popupId} role='dialog'>cool popup</div>}
   * />
   */
  'aria-controls': string;

  /**
   * Should be set to match the `role` of the popup content, i.e. `'menu' | 'listbox' | 'tree' | 'grid' | 'dialog'`.
   */
  'aria-haspopup'?: ButtonAttrs['aria-haspopup'];

  /** A ref to the button element, e.g. for positioning an adjacent popup. */
  buttonRef?: React.Ref<HTMLButtonElement>;

  /** The content rendered in the dropdown button. */
  children?: React.ReactNode;

  /** NOTE: applied to the root `<div>`, not the dropdown button element. */
  className?: string;

  /**
   * When true, the dropdown will close when focus leaves the popup.
   */
  closeOnPopupBlur?: boolean;

  disabled?: boolean;

  /**
   * When true, the DropdownButton element will be focused whenever the Dropdown closes.
   * @default ```true```
   */
  focusOnClose?: boolean;

  /** Label to display above the field. */
  label: React.ReactNode;

  /**
   * If provided, the `<label>` element will use this string as its DOM id, rather than the default auto-generated value.
   * Primarily useful for referencing in e.g. `aria-labelledby` on a DOM-distant element.
   */
  labelId?: string;

  /**
   * The contents of the helper message under the field, if any.
   * Set to `<br />` to reserve vertical space for showing a validation message when `valid` is `undefined`.
   */
  message?: React.ReactNode;

  /** Callback invoked when the Dropdown closes. */
  onClose?: () => void;

  /**
   * Callback invoked when the Dropdown opens.
   * @param done may be saved to be called when the user interaction inside the popup is complete (e.g. when an item is selected),
   *             signaling the Dropdown to close. Alternatively, our suggestion is to save a ref to this component, and `toggle(false)` on it.
   * @param rect the bounding ClientRect of the DropdownButton element. Probably only useful for Hybrid UI on Desktop.
   */
  onOpen?: (done: () => void, rect: ClientRect) => void;

  /**
   * Which side of the dropdown button the popup aligns against.
   * @default ```'left'```
   */
  popupAlignment?: 'left' | 'right';

  /**
   * The content to render inside the popup. Omitting will prevent the popup from displaying at all.
   */
  popupContent?: React.ReactNode;

  /**
   * @deprecated this is a flaky API and should never be used. Instead use `popupContent`.
   * @returns the content to render inside the popup. Omitting will prevent the popup from displaying at all.
   */
  renderPopupContent?: () => React.ReactNode;

  /** Added to the button element */
  testId?: string;

  /** @default ```DropdownButtonType.Line``` */
  type?: DropdownButtonType;

  useDarkStyling?: boolean;

  /**
   * * `undefined` or `null`: the field will be shown with normal coloring
   * * `"pending"`: the field will be shown with normal coloring and a loading animation
   * * `false`: the field will be shown with red coloring, and `aria-errormessage`/`aria-invalid` will be set on the `<input>`
   * * `true`: the field will be shown with green coloring
   * @default ```undefined```
   */
  valid?: boolean | 'pending';

  /**
   * Should be used only for overriding stacking errors in VizClient.
   * @default ```1```
   */
  zIndex?: number;
};

export type DropdownButtonState = Pick<InteractionState, 'active' | 'focus' | 'hover'> & {
  isOpen: boolean;
};

/**
 * A button which displays a popup when pressed.
 * The contents of the popup are provided by you!
 * If you're looking for the equivalent of an HTML `<select>`, use `SingleSelectDropdown`.
 */
export class DropdownButton extends React.PureComponent<DropdownButtonProps & Omit<ButtonAttrs, keyof DropdownButtonProps>, DropdownButtonState> {
  public state: DropdownButtonState = { isOpen: false };
  public static defaultProps: Pick<DropdownButtonProps, 'focusOnClose' | 'popupAlignment' | 'type' | 'zIndex'> = {
    focusOnClose: true,
    popupAlignment: 'left',
    type: DropdownButtonType.Line,
    zIndex: 1,
  };

  private unsubscribeFromGlobalMousedown: (() => void) | undefined = undefined;
  private dropdownButtonElement: HTMLElement | null = null;
  private stackingRootRef: React.RefObject<HTMLDivElement> = React.createRef();
  private dropdownOverlayRef: React.RefObject<DropdownOverlay> = React.createRef();
  private defaultPopupId: string = generateId(); // prop was not previously required, so many run-time instances may not yet provide it
  private mouseDownOnButton: boolean = false; // see https://gitlab.tableausoftware.com/browser-clients/widgets/merge_requests/1406
  private labelId: string = generateId();
  private getLabelId = () => this.props.labelId || this.labelId;
  private buttonId: string = generateId();
  private getButtonId = () => this.props.id || this.buttonId;

  public componentDidUpdate(prevProps: DropdownButtonProps, prevState: DropdownButtonState): void {
    if (!prevProps.disabled && this.props.disabled) {
      if (this.state.isOpen) {
        this.close();
      }
      this.setState({ active: false, hover: false, focus: false });
    }

    if (!prevState.isOpen && this.state.isOpen) {
      this.onOpened();
    } else if (prevState.isOpen && !this.state.isOpen) {
      this.onClosed();
    }
  }

  public componentWillUnmount(): void {
    this.unsubscribeFromGlobalMousedown?.();
    this.unsubscribeFromGlobalMousedown = undefined;
  }

  private handleGlobalMousedown = (e: MouseEvent): void => {
    const target = e.target as HTMLElement;
    const popupId = this.dropdownButtonElement!.getAttribute('aria-controls')!;
    const overlayElem = document.getElementById(popupId);
    if (!overlayElem) {
      throw new Error(`DropdownButton with textContent "${this.dropdownButtonElement!.textContent}" and testId "${this.props.testId}": found no popup element with id matching aria-controls value "${popupId}"`);
    }
    if (!(overlayElem.contains(target) || this.dropdownButtonElement!.contains(target))) {
      this.mouseDownOnButton = false;
      this.close();
    }
  }

  private handleClick: React.MouseEventHandler<HTMLButtonElement> = mergeEventHandlers(e => {
    if (e.currentTarget.contains(e.target as HTMLElement)) {
      this.toggle();
    }
  }, e => this.props.onClick?.(e));

  private handleMouseDown: React.MouseEventHandler<HTMLButtonElement> = mergeEventHandlers(e => {
    this.mouseDownOnButton = true;
  }, e => this.props.onMouseDown?.(e));

  private handleMouseUp: React.MouseEventHandler<HTMLButtonElement> = mergeEventHandlers(e => {
    this.mouseDownOnButton = false;
  }, e => this.props.onMouseUp?.(e));

  private handleFocusChange: React.FocusEventHandler<HTMLButtonElement> = e => {
    if (e.target === e.currentTarget) {
      if (e.type === 'focus' && this.mouseDownOnButton) {
        // TFSID 1116354 don't flash a focus indicator if the dropdown is being clicked
        e.currentTarget.blur();
      } else {
        this.setState({ focus: e.type === 'focus' });
      }
    }
  }

  private handleFocus: React.FocusEventHandler<HTMLButtonElement> =
    mergeEventHandlers(this.handleFocusChange, e => this.props.onFocus?.(e));
  private handleBlur: React.FocusEventHandler<HTMLButtonElement> =
    mergeEventHandlers(this.handleFocusChange, e => this.props.onBlur?.(e));
  private handleHover: React.MouseEventHandler<HTMLButtonElement> =
    e => this.setState({ hover: !this.props.disabled && e.type === 'mouseover' })

  private handleKeyDown: React.KeyboardEventHandler<HTMLButtonElement> = e => {
    // swallow ESC events that plausibly originate from focus being returned to the button after the popup closed
    if (e.key === 'Escape' && e.repeat) {
      e.preventDefault();
      e.stopPropagation();
    }

    this.props.onKeyDown?.(e);
    if (e.defaultPrevented) {
      return;
    }

    if (e.key === 'ArrowDown' && !e.repeat) {
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.click();
    }
  }

  private handlePortalKeyDown = (e: React.KeyboardEvent<HTMLElement>): void => {
    if (e.key === 'Escape' && this.state.isOpen) {
      this.close();
      e.preventDefault();
    }
    e.stopPropagation();
  }

  private handleButtonRef = (ref: HTMLButtonElement | null): void => {
    this.dropdownButtonElement = ref;
    assignRef(this.props.buttonRef, ref);
  }

  private close = (): void => {
    this.toggle(false);
  }

  private handleTrap: React.FocusEventHandler<HTMLElement> = e => {
    // if the trap occurs because of a clickEvent on the DropdownButton, we let the ClickHandler trigger the close
    if (!this.mouseDownOnButton) {
      this.close();
    }
    this.mouseDownOnButton = false;
  }

  /**
   * Toggles the dropdown popup.
   * @param open optional parameter, ```true``` to open the dropdown or ```false``` to close it.
   */
  public toggle(open: boolean = !this.state.isOpen): void {
    if (this.props.disabled && open) { return; }
    if (open === this.state.isOpen) { return; }

    this.setState({ isOpen: open });
  }

  /**
   * Repositions the popup, if it is currently shown. Might be useful if the popup contents weren't finished sizing at the time that the
   * popup was shown, e.g. if it contained an image which was still loading.
   */
  public repositionPopup(): void {
    this.dropdownOverlayRef.current?.reposition();
  }

  private onOpened = (): void => {
    // tslint:disable-next-line:deprecation
    if (this.props.popupContent || this.props.renderPopupContent) {
      this.unsubscribeFromGlobalMousedown = GlobalPointerDownOutsideListener.subscribe(
        this.dropdownButtonElement!,
        this.handleGlobalMousedown
      );
    }
    this.props.onOpen?.(() => this.close(), this.dropdownButtonElement!.getBoundingClientRect());
  }

  private onClosed = (): void => {
    this.unsubscribeFromGlobalMousedown?.();
    this.unsubscribeFromGlobalMousedown = undefined;

    if (this.props.focusOnClose) {
      this.dropdownButtonElement!.focus();
    }

    this.props.onClose?.();
  }

  public render(): JSX.Element {
    // tslint:disable-next-line:max-line-length deprecation
    const { buttonRef, className, closeOnPopupBlur, focusOnClose, label, labelId, message, onClose, onOpen, popupAlignment, popupContent, renderPopupContent, stackingRootId, testId, type, useDarkStyling, valid, zIndex, ...htmlProps } = this.props;
    const labeledFieldProps = { disabled: htmlProps.disabled, id: this.getButtonId(), label, labelId: this.getLabelId(), message, valid };

    return (
      <LabeledField {...labeledFieldProps}
        className={TypeStyle.classes(className, { dark: useDarkStyling, [DisplayFlex]: type !== DropdownButtonType.Icon })}
      >
        {fieldProps =>
          <StackingRootContext.Consumer>{stackingRoot => <>
            {/* If no stacking root was provided, we render the popup here */}
            {!stackingRootId && !stackingRoot && <div style={DropdownButtonStyles.PopupContainerStyle} ref={this.stackingRootRef} />}
            <button
              {...fieldProps}
              {...EventUtilsOptOut}
              aria-haspopup={true}
              aria-labelledby={[this.getLabelId(), this.getButtonId()].join(' ')}
              data-tb-test-id={testId}
              {...htmlProps}
              aria-atomic={true}
              aria-controls={this.state.isOpen ? (this.props['aria-controls'] || this.defaultPopupId) : undefined}
              aria-expanded={!!this.state.isOpen}
              aria-live={this.state.focus && !this.state.isOpen ? 'assertive' : 'off'}
              aria-relevant='text'
              className={TypeStyle.classes(DropdownButtonStyles.ClassName, TypeNames[type!])}
              onBlur={this.handleBlur}
              onClick={this.handleClick}
              onFocus={this.handleFocus}
              onKeyDown={this.handleKeyDown}
              onMouseOut={this.handleHover}
              onMouseOver={this.handleHover}
              onMouseDown={this.handleMouseDown}
              onMouseUp={this.handleMouseUp}
              ref={this.handleButtonRef}
              tabIndex={htmlProps.disabled ? undefined : (htmlProps.tabIndex || 0)}
            >
              {this.renderChildren()}
              {this.renderIcon()}
              {this.state.isOpen && (popupContent || renderPopupContent) &&
                <StackingRootContext.Provider value={stackingRoot || this.stackingRootRef.current}>
                  <DropdownPopup
                    anchorEl={this.dropdownButtonElement!}
                    children={popupContent || renderPopupContent?.()}
                    onKeyDown={this.handlePortalKeyDown}
                    onTrap={closeOnPopupBlur ? this.handleTrap : undefined}
                    // TFS 961939 if `aria-controls was not provided, we add a default id for the popup;
                    // this does not fulfill the ARIA spec but prevents broken assumptions
                    overlayId={!this.props['aria-controls'] ? this.defaultPopupId : undefined}
                    overlayRef={this.dropdownOverlayRef}
                    popupAlignment={popupAlignment!}
                    stackingRootId={stackingRootId}
                    zIndex={zIndex}
                  />
                </StackingRootContext.Provider>
              }
            </button>
          </>}</StackingRootContext.Consumer>
        }
      </LabeledField>
    );
  }

  private renderChildren(): React.ReactNode {
    if (this.props.children === undefined || this.props.children === null) { return null; }
    return <span className={DropdownButtonStyles.ChildrenClassName}>{this.props.children}</span>;
  }

  private renderIcon(): JSX.Element {
    const svgIcon: DynamicSvgIcon = this.props.type === DropdownButtonType.Icon ? IconDropdownArrowIcon : DropdownArrowIcon;
    const src: string = svgIcon.icon({
      active: this.state.active || this.state.isOpen,
      disabled: this.props.disabled,
      hover: this.state.hover,
      onDarkBackground: this.props.useDarkStyling,
    });

    return <Img src={src} style={{ margin: this.props.children ? undefined : `0 auto` }} />;
  }
}

type DropdownPopupProps =
  Pick<StackingComponentProps, 'stackingRootId' | 'zIndex'> &
  Pick<FocusTrapProps, 'onTrap'> &
  {
    anchorEl: HTMLElement,
    popupAlignment: 'left' | 'right',
    onKeyDown: React.KeyboardEventHandler,
    overlayId?: string,
    overlayRef: React.Ref<DropdownOverlay>;
  };

const DropdownPopup: React.FC<DropdownPopupProps> = React.memo(props => {
  // hooks have to be called the same number of times in every render, so these need to be called before the potential early return
  const getAnchorClientRect = React.useCallback(() => props.anchorEl.getBoundingClientRect(), [props.anchorEl]);
  const portalEventTrapOverrides = React.useMemo(() => ({ onKeyDown: props.onKeyDown }), [props.onKeyDown]);

  return !props.children ? null :
    <DropdownOverlay
      alignment={props.popupAlignment}
      getAnchorClientRect={getAnchorClientRect}
      portalEventTrapOverrides={portalEventTrapOverrides}
      // tslint:disable-next-line:deprecation
      stackingRootId={props.stackingRootId}
      zIndex={props.zIndex}
    >
      <FocusTrap
        children={props.children}
        id={props.overlayId}
        style={{ ...PopupStyle, minWidth: props.anchorEl?.getBoundingClientRect().width }}
        onTrap={props.onTrap}
      />
      {props.onTrap &&
        // TFS 1080312 Add invisible div as "next thing to receive focus", allowing a document-final dropdown to close on TAB
        <div {...AxeOptOut} aria-hidden tabIndex={0} />
      }
    </DropdownOverlay>;
});
DropdownPopup.displayName = 'DropdownPopup';
